import 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {O_1Page} from '@/pages/components/audit-program/o-section/constants';
import {O_1_PAGE} from '@/pages/components/audit-program/o-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface O_1EquityViewProps {
    header: AuditProgramSectionVO;
    page: O_1Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.75rem', py: 0.75, px: 0.5};
const bodySx = {...cellBorder, py: 0.5, px: 0.5, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** O_1 — Share capital / Equity amount schedule. */
export default function O_1EquityView({
    header,
    page,
    refCode = 'O_1',
    values,
    onChange,
}: O_1EquityViewProps) {
    const meta = {
        title: page.title || O_1_PAGE.title,
        subtitle: page.subtitle ?? O_1_PAGE.subtitle,
        amountLabel: page.amountLabel || O_1_PAGE.amountLabel,
        cyLabel: page.cyLabel || O_1_PAGE.cyLabel,
        lyLabel: page.lyLabel || O_1_PAGE.lyLabel,
        conclusionLabel: page.conclusionLabel || O_1_PAGE.conclusionLabel,
        conclusionPlaceholder: page.conclusionPlaceholder || O_1_PAGE.conclusionPlaceholder,
        remarksTitle: page.remarksTitle || O_1_PAGE.remarksTitle,
        remarks: page.remarks?.length ? page.remarks : O_1_PAGE.remarks,
    };

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 0.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>
            {meta.subtitle ? (
                <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', mb: 1.5}}>
                    {meta.subtitle}
                </Typography>
            ) : (
                <Box sx={{mb: 1.5}}/>
            )}

            <TableContainer sx={{mb: 2, maxWidth: 640}}>
                <Table size="small" sx={{tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col/>
                        <col style={{width: 48}}/>
                        <col style={{width: 140}}/>
                        <col style={{width: 48}}/>
                        <col style={{width: 140}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx} align="right">{meta.cyLabel}</TableCell>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx} align="right">{meta.lyLabel}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell sx={bodySx}>
                                <Typography variant="body2">{meta.amountLabel}</Typography>
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="center"
                                    sx={fieldSx}
                                    value={values['amount.tickCy'] ?? ''}
                                    onChange={(e) => onChange('amount.tickCy', e.target.value)}
                                />
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="right"
                                    sx={fieldSx}
                                    value={values['amount.cy'] ?? ''}
                                    onChange={(e) => onChange('amount.cy', e.target.value)}
                                />
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="center"
                                    sx={fieldSx}
                                    value={values['amount.tickLy'] ?? ''}
                                    onChange={(e) => onChange('amount.tickLy', e.target.value)}
                                />
                            </TableCell>
                            <TableCell sx={bodySx}>
                                <InputField
                                    type="text"
                                    fullWidth
                                    compact
                                    align="right"
                                    sx={fieldSx}
                                    value={values['amount.ly'] ?? ''}
                                    onChange={(e) => onChange('amount.ly', e.target.value)}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                {meta.conclusionLabel}
            </Typography>
            <InputField
                type="text"
                fullWidth
                compact
                multiline
                minRows={2}
                placeholder={meta.conclusionPlaceholder}
                value={values.conclusion ?? ''}
                onChange={(e) => onChange('conclusion', e.target.value)}
                sx={{mb: 2, maxWidth: 640}}
            />

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.5}}>
                {meta.remarksTitle}
            </Typography>
            <Stack spacing={0.25}>
                {meta.remarks.map((r, idx) => (
                    <Typography key={`${r.tick}-${idx}`} variant="body2" sx={{pl: 0.5, lineHeight: 1.4}}>
                        <Box component="span" sx={{display: 'inline-block', minWidth: 44, fontWeight: 600}}>
                            {r.tick}
                        </Box>
                        {r.text}
                    </Typography>
                ))}
            </Stack>
        </Box>
    );
}
